import React, { useState, useEffect } from 'react';
import {
  Box,
  Drawer,
  Typography,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  LinearProgress,
  Alert,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import BlockIcon from '@mui/icons-material/Block';
import { detectThemeSync, watchThemeChanges } from '../../utils/themeUtils';
import { getDashboardDarkSurfaceTokens } from '../../utils/dashboardSurfaceSx';
import {
  getBookingDrawerPaperSx,
  getBookingDrawerTextFieldSx,
  getBookingDrawerHeaderSx,
  getBookingDrawerFooterSx,
  getBookingDrawerTheme,
  getBookingDrawerOutlinedButtonSx,
  getBookingDrawerPrimaryButtonSx,
  getGuestDrawerContentSx,
} from '../../utils/bookingDrawerLayout';
import {
  getDistributionSectionIconBoxSx,
  isDistributionMobileLayout,
} from '../../utils/distributionMobileLayout';
import { THEME_COLORS } from '../../shared/constants/theme';
import { getDrawerRootSx } from '../../utils/drawerOverlayProps';
import EventBlockAdjustView from './EventBlockAdjustView';

const THEME_COLOR = THEME_COLORS.PRIMARY;

const EventBlockDrawer = ({
  open,
  onClose,
  onSave,
  isSaving = false,
  errorMessage = null,
  onErrorMessageChange,
  inventoryBlocks = [],
  loading = false,
  onRefresh,
  onEdit,
  onToggleActive,
  formData,
  onFormDataChange,
  errors,
  onErrorsChange,
  allRoomTypes = [],
  channels = [],
  getInventoryData,
  editingBlock = null,
  onDeleteConfirm,
  deleteDialogOpen = false,
  onDeleteDialogClose,
  blockToDelete = null,
  initialMode = 'add',
}) => {
  const [currentTheme, setCurrentTheme] = useState(() => detectThemeSync());
  const isDark = currentTheme === 'dark';
  const isMobileLayout = isDistributionMobileLayout();
  const drawerTheme = getBookingDrawerTheme(isDark);
  const themeColors = getDashboardDarkSurfaceTokens(isDark);
  const [mode, setMode] = useState(initialMode === 'manage' ? 'delete' : 'add');

  useEffect(() => {
    const cleanup = watchThemeChanges((newTheme) => {
      setCurrentTheme(newTheme);
    });
    return cleanup;
  }, []);

  useEffect(() => {
    if (editingBlock) {
      setMode('add');
    }
  }, [editingBlock]);

  useEffect(() => {
    if (!open) {
      setMode('add');
      return;
    }
    setMode(initialMode === 'manage' ? 'delete' : 'add');
  }, [open, initialMode]);

  const handleClose = () => {
    onClose();
    setMode('add');
  };

  const handleEdit = (block) => {
    setMode('add');
    onEdit?.(block);
  };

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={handleClose}
        sx={getDrawerRootSx()}
        PaperProps={{
          sx: getBookingDrawerPaperSx(isDark, {
            width: { xs: '100%', sm: 600 },
            maxWidth: '100vw',
            display: 'flex',
            flexDirection: 'column',
            p: 0,
          }),
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', position: 'relative' }}>
          <Box
            sx={{
              ...getBookingDrawerHeaderSx(isDark),
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              p: 1.5,
              position: 'relative',
              flexShrink: 0,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
              <Box sx={getDistributionSectionIconBoxSx(THEME_COLOR, isMobileLayout)}>
                <BlockIcon sx={{ fontSize: 22, color: THEME_COLOR }} />
              </Box>
              <Typography variant="h6" sx={{ color: drawerTheme.text, fontWeight: 700, fontSize: '1.05rem' }}>
                {editingBlock ? 'Edit Inventory Block' : 'Add Inventory Block'}
              </Typography>
            </Box>
            <IconButton
              onClick={handleClose}
              disabled={isSaving}
              size="small"
              sx={{
                color: drawerTheme.subtleText,
                '&:hover': { backgroundColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)' },
              }}
            >
              <CloseIcon />
            </IconButton>
            {isSaving && (
              <LinearProgress
                sx={{
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  right: 0,
                  height: 2,
                  backgroundColor: 'transparent',
                  '& .MuiLinearProgress-bar': {
                    backgroundColor: THEME_COLOR,
                  },
                }}
              />
            )}
          </Box>

          <Box
            sx={{
              flex: 1,
              overflowY: 'auto',
              overflowX: 'hidden',
              p: 1.5,
              pt: 1.25,
              pr: 2.5,
              ...getGuestDrawerContentSx(isDark),
              ...getBookingDrawerTextFieldSx(isDark),
              '& .MuiOutlinedInput-root.Mui-focused fieldset': {
                borderColor: THEME_COLOR,
              },
              '& .MuiInputLabel-root.Mui-focused': {
                color: THEME_COLOR,
              },
              '&::-webkit-scrollbar': {
                width: '8px',
              },
              '&::-webkit-scrollbar-track': {
                background: 'transparent',
              },
              '&::-webkit-scrollbar-thumb': {
                background: isDark ? '#555' : '#ccc',
                borderRadius: '4px',
                '&:hover': {
                  background: isDark ? '#666' : '#aaa',
                },
              },
            }}
          >
            {errorMessage && (
              <Alert
                severity="error"
                onClose={() => onErrorMessageChange && onErrorMessageChange(null)}
                sx={{ mb: 2 }}
              >
                {errorMessage}
              </Alert>
            )}

            <EventBlockAdjustView
              mode={mode}
              onModeChange={setMode}
              inventoryBlocks={inventoryBlocks}
              loading={loading}
              isSaving={isSaving}
              onRefresh={onRefresh}
              onEdit={handleEdit}
              onToggleActive={onToggleActive}
              formData={formData}
              onFormDataChange={onFormDataChange}
              errors={errors}
              onErrorsChange={onErrorsChange}
              allRoomTypes={allRoomTypes}
              isDark={isDark}
              channels={channels}
              getInventoryData={getInventoryData}
              editingBlock={editingBlock}
            />
          </Box>

          <Box sx={{ ...getBookingDrawerFooterSx(isDark), p: 1.5, flexShrink: 0 }}>
            <Box sx={{ display: 'flex', gap: 1.5 }}>
              <Button
                variant="outlined"
                fullWidth
                onClick={handleClose}
                disabled={isSaving}
                sx={getBookingDrawerOutlinedButtonSx(isDark)}
              >
                Close
              </Button>
              <Button
                variant="contained"
                fullWidth
                onClick={onSave}
                disabled={isSaving || loading}
                sx={getBookingDrawerPrimaryButtonSx(isDark)}
              >
                {isSaving ? 'Saving...' : (editingBlock ? 'Update Block' : 'Create Block')}
              </Button>
            </Box>
          </Box>
        </Box>
      </Drawer>

      <Dialog
        open={deleteDialogOpen}
        onClose={onDeleteDialogClose}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: isDark ? themeColors.surface : '#ffffff',
            borderRadius: 3,
            margin: 2,
          },
        }}
        BackdropProps={{
          sx: {
            backgroundColor: isDark ? 'rgba(0, 0, 0, 0.7)' : 'rgba(0, 0, 0, 0.5)',
          },
        }}
      >
        <DialogTitle sx={{ color: themeColors.text }}>Delete Inventory Block</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: themeColors.subtleText }}>
            Are you sure you want to delete the inventory block for "{blockToDelete?.room_type_name || `Room Type ${blockToDelete?.room_type_id}`}"?
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={onDeleteDialogClose}
            sx={{ color: themeColors.subtleText }}
          >
            Cancel
          </Button>
          <Button
            onClick={onDeleteConfirm}
            color="error"
            variant="contained"
            disabled={isSaving}
          >
            {isSaving ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default EventBlockDrawer;
